import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Bell, CheckCircle2, ClipboardCheck, TriangleAlert, X } from "lucide-react";
import { Badge, Button, Dialog, DialogActions, IconButton, Table } from "@studio";
import { api, errorMessage } from "../lib/api";
import { can } from "../lib/access";
import { fmtDateTime, fmtTime } from "../lib/format";
import { useAdminMachines, useAdminRuns, useOverview, useResultReviewSummary } from "../lib/queries";
import { useCustomerDetailFocus } from "./customer-detail";
import type { Me } from "../lib/types";
import { ErrorBox, Loading, Panel } from "../console/ui";
import { RawDebugDetails } from "@studio-features/meta-crew/readable-details";

/**
 * 머신 장애 (시안 6a084bb incidents.tsx 를 실제 데이터로).
 * 코어가 스스로 판정한 장애(재시작 후 미복귀, 응답 없음·네트워크 끊김)와 브릿지가 보낸 장애 보고를 직원이 본다.
 */

export type StatusEvent = { at: string; kind: string; detail: Record<string, unknown> | null; machineId: string; machineLabel: string; customerId: string; customerName: string };

const outageReasonNames: Record<string, string> = {
  server_unreachable: "서버 응답 없음",
  network: "PC 네트워크 문제",
  app_restart: "프로그램 비정상 종료",
  token_rejected: "인증 거부",
  unknown: "원인 미상",
};
const disconnectReasonNames: Record<string, string> = {
  client: "프로그램 종료",
  transport: "네트워크 끊김",
  heartbeat_timeout: "응답 없음",
  server_restart: "서버 재시작",
};

const dur = (sec: number): string => (sec < 60 ? `${sec}초` : sec < 3600 ? `${Math.round(sec / 60)}분` : `${Math.round(sec / 360) / 10}시간`);

/** 상태 이력 한 줄을 사람이 읽는 문장으로. 고객 화면·관리자 화면 공용. */
export function eventText(e: StatusEvent): string {
  const d = e.detail ?? {};
  switch (e.kind) {
    case "connected":
      return `서버에 연결${d.version ? ` · v${d.version}` : ""}`;
    case "disconnected":
      return `서버 연결 끊김${typeof d.reason === "string" && disconnectReasonNames[d.reason] ? ` · ${disconnectReasonNames[d.reason]}` : ""}`;
    case "reconnect_missing":
      return `서버 재시작 후 ${dur(Number(d.graceSec ?? 120))} 넘게 다시 연결되지 않음`;
    case "outage_reported":
      return `연결 장애 보고 · ${outageReasonNames[String(d.reason)] ?? "원인 미상"} · ${dur(Number(d.durationSec ?? 0))}${Number(d.attempts) ? ` · 재시도 ${d.attempts}회` : ""}`;
    case "llm_available":
      return "AI 사용 가능";
    case "llm_unavailable":
      return d.reason === "not_installed" ? "AI 프로그램 없음" : "AI 로그인 필요";
    case "llm_throttled":
      return `AI 사용 한도 도달${d.until ? ` · ${fmtDateTime(String(d.until))} 복구 예상` : ""}`;
    case "device_attached":
      return `폰 연결 · ${String(d.model ?? d.serial ?? "")}`;
    case "device_detached":
      return `폰 분리 · ${String(d.model ?? d.serial ?? "")}`;
    case "version_changed":
      return `프로그램 버전 변경 · ${String(d.from ?? "")} → ${String(d.to ?? "")}`;
    default:
      return e.kind;
  }
}

const eventKey = (e: StatusEvent) => `${e.machineId}|${e.kind}|${e.at}`;

/** 장애 이력 (직원 전용 API). 머신 신호가 오면 realtime 이 "status-history" 키를 무효화한다. */
export const useIncidents = (hours = 24, enabled = true) =>
  useQuery({
    queryKey: ["status-history", "incidents", hours],
    queryFn: () => api.page<StatusEvent>(`/admin/machines/status-history?incidents=1&hours=${hours}`),
    refetchInterval: 60_000,
    enabled,
  });

type Incident = {
  key: string;
  e: StatusEvent;
  kind: string;
  reason: string;
  recovered: boolean;
  detail: string;
  attempts: string;
  events: string;
};

/** 이력 한 줄을 장애 행으로. 복귀 여부는 지금 머신이 연결돼 있는지로 본다 (브릿지 장애 보고는 복귀한 뒤에 오므로 항상 복귀). */
function toIncident(e: StatusEvent, connectedNow: Map<string, boolean>): Incident {
  const d = e.detail ?? {};
  const connected = connectedNow.get(e.machineId) ?? false;
  if (e.kind === "outage_reported") {
    return {
      key: eventKey(e),
      e,
      kind: "브릿지 장애 보고",
      reason: outageReasonNames[String(d.reason)] ?? "원인 미상",
      recovered: true,
      detail: `${fmtTime(String(d.disconnectedAt ?? e.at))} 끊김 → ${fmtTime(String(d.reconnectedAt ?? e.at))} 복귀 · ${dur(Number(d.durationSec ?? 0))}`,
      attempts: `${Number(d.attempts ?? 0)}회`,
      events: `${Number(d.outboxQueued ?? 0)}건 재전송`,
    };
  }
  if (e.kind === "reconnect_missing") {
    return {
      key: eventKey(e),
      e,
      kind: "재연결 안 됨",
      reason: `서버 재시작 후 ${Number(d.graceSec ?? 120)}초 내 미접속`,
      recovered: connected,
      detail: `마지막 수신 ${d.lastSeenAt ? fmtDateTime(String(d.lastSeenAt)) : "알 수 없음"} · ${connected ? "지금은 연결됨" : "전원과 현장 네트워크 확인 필요"}`,
      attempts: "확인 불가",
      events: "확인 불가",
    };
  }
  return {
    key: eventKey(e),
    e,
    kind: d.reason === "heartbeat_timeout" ? "응답 없음 끊김" : "네트워크 끊김",
    reason: disconnectReasonNames[String(d.reason)] ?? "원인 미상",
    recovered: connected,
    detail: `${fmtTime(e.at)} 끊김 · ${connected ? "지금은 연결됨" : "아직 미복귀"}`,
    attempts: "브릿지 보고 대기",
    events: "브릿지 보고 대기",
  };
}

/** 모니터링 > 시스템 지표 맨 위 "머신 장애 · 24시간". */
export function MachineIncidents({ customerId }: { customerId?: string }) {
  const q = useIncidents();
  const machines = useAdminMachines();
  const [selected, setSelected] = useState<Incident | null>(null);
  const connectedNow = new Map((machines.data?.items ?? []).map((m) => [m.id, m.connected]));
  const rows = (q.data?.items ?? []).filter((e) => !customerId || e.customerId === customerId).map((e) => toIncident(e, connectedNow));
  const open = rows.filter((r) => !r.recovered).length;
  return (
    <Panel title="머신 장애 · 24시간" extra={<Badge tone={open ? "coral" : "teal"}>미복귀 {open}건</Badge>}>
      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <ErrorBox error={errorMessage(q.error)} onRetry={() => void q.refetch()} />
      ) : rows.length === 0 ? (
        <p className="mcc-padding meta-crew-muted">최근 24시간 장애가 없습니다.</p>
      ) : (
        <Table>
          <thead>
            <tr>
              {["발생 / 머신", "장애 구분", "원인", "복귀 상태", ""].map((s, i) => (
                <th key={i}>{s}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <td>
                  <strong>{r.e.machineLabel}</strong>
                  <small className="meta-crew-sub">
                    {r.e.customerName} · {fmtTime(r.e.at)}
                  </small>
                </td>
                <td>{r.kind}</td>
                <td>{r.reason}</td>
                <td>
                  <Badge tone={r.recovered ? "teal" : "coral"}>{r.recovered ? "복귀 완료" : "미복귀"}</Badge>
                </td>
                <td>
                  <Button size="sm" variant="ghost" onClick={() => setSelected(r)}>
                    장애 상세
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <Dialog
        open={!!selected}
        onOpenChange={(v) => !v && setSelected(null)}
        title={`${selected?.e.machineLabel ?? "머신"} 장애 상세`}
        size="wide"
        footer={<DialogActions primary={{ label: "확인", onClick: () => setSelected(null) }} />}
      >
        {selected && (
          <div className="lwi-incident-detail">
            <Badge tone={selected.recovered ? "teal" : "coral"}>{selected.recovered ? "복귀 완료" : "현장 조치 필요"}</Badge>
            <h3>{selected.kind}</h3>
            <p>{selected.detail}</p>
            <dl>
              <div>
                <dt>끊김 원인</dt>
                <dd>{selected.reason}</dd>
              </div>
              <div>
                <dt>재시도</dt>
                <dd>{selected.attempts}</dd>
              </div>
              <div>
                <dt>보관 이벤트</dt>
                <dd>{selected.events}</dd>
              </div>
            </dl>
            <RawDebugDetails rawText={JSON.stringify({ kind: selected.e.kind, at: selected.e.at, ...(selected.e.detail ?? {}) }, null, 2)} canRead />
            <p className="meta-crew-muted">고객 연결 이력에는 원인만 표시하며 원문 오류는 공개하지 않습니다.</p>
          </div>
        )}
      </Dialog>
    </Panel>
  );
}

/* ---------- 직원 운영 알림 (헤더 알림함 + 우측 지속 알림) ---------- */

type Notice = { kind: "incident" | "review" | "publish"; title: string; body: string; at: number };

/**
 * 장애 신호(미복귀·새 장애)와 검토 대기 증가를 실시간 신호로 받아 알린다. 메뉴를 옮겨도 남고, 닫거나 목적 화면으로 가면 사라진다.
 * 처음 불러온 목록은 이미 본 것으로 둔다.
 */
export function StaffNotifications({ me, onNavigate }: { me: Me; onNavigate: (screen: string) => void }) {
  const canIncidents = can(me, "SYSTEM_READ");
  const canReview = can(me, "TASK_DETAIL");
  const incidents = useIncidents(24, canIncidents);
  const machines = useAdminMachines();
  const summary = useResultReviewSummary("", canReview);
  const overview = useOverview();
  const seen = useRef<Set<string> | null>(null);
  const lastPending = useRef<number | null>(null);
  const lastPublish = useRef<number | null>(null);
  // 2026-10-16 고객 발행: 확인 대기 발행 요청·새 답글.
  const publishPending = overview.data?.publishPending ?? 0;
  const publishUnread = overview.data?.publishUnread ?? 0;
  // 시안 951f2f6: 고객사별 확인 대기·새 답글 묶음 → 고객사 상세 › 발행 요청 탭으로.
  const publishRuns = useAdminRuns({ origin: "customer", limit: 100 }, canReview && publishPending + publishUnread > 0);
  const publishGroups = (() => {
    const by = new Map<string, { id: string; name: string; pending: number; unread: number }>();
    for (const r of publishRuns.data?.items ?? []) {
      if (!r.customerId) continue;
      const g = by.get(r.customerId) ?? { id: r.customerId, name: r.customerName ?? "고객사", pending: 0, unread: 0 };
      if (r.status === "held" && r.errorKind === "approval_required") g.pending++;
      if ((r.unreadForStaff ?? 0) > 0) g.unread++;
      by.set(r.customerId, g);
    }
    return [...by.values()].filter((g) => g.pending || g.unread);
  })();
  const [notice, setNotice] = useState<Notice | null>(null);
  const [open, setOpen] = useState(false);
  const connectedNow = new Map((machines.data?.items ?? []).map((m) => [m.id, m.connected]));
  const rows = (incidents.data?.items ?? []).map((e) => toIncident(e, connectedNow));
  const unrecovered = rows.filter((r) => !r.recovered);
  const pending = summary.data?.pending ?? 0;

  useEffect(() => {
    const items = incidents.data?.items;
    if (!items) return;
    if (!seen.current) {
      seen.current = new Set(items.map(eventKey));
      return;
    }
    const fresh = items.filter((e) => !seen.current!.has(eventKey(e)));
    if (!fresh.length) return;
    for (const e of fresh) seen.current.add(eventKey(e));
    const e = fresh[0]!;
    const inc = toIncident(e, connectedNow);
    setNotice({ kind: "incident", title: inc.recovered ? `${e.machineLabel} 연결 장애가 복구되었습니다` : `${e.machineLabel}가 다시 연결되지 않았습니다`, body: `${e.customerName} · ${inc.kind} · ${inc.reason}`, at: Date.now() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidents.data]);
  useEffect(() => {
    if (summary.data === undefined) return;
    if (lastPending.current === null) {
      lastPending.current = pending;
      return;
    }
    if (pending > lastPending.current) setNotice({ kind: "review", title: `검토 대기 결과 ${pending}건`, body: "AI 판정과 캡처를 대조하고 게시 결과를 검토하세요.", at: Date.now() });
    lastPending.current = pending;
  }, [pending, summary.data]);

  useEffect(() => {
    if (!canReview || overview.data === undefined) return;
    const n = publishPending + publishUnread;
    if (lastPublish.current === null) {
      lastPublish.current = n;
      return;
    }
    if (n > lastPublish.current) setNotice({ kind: "publish", title: publishPending ? `확인 대기 발행 요청 ${publishPending}건` : `고객 새 답글 ${publishUnread}건`, body: "고객이 보낸 원고를 확인하고 승인·수정 요청·반려하세요.", at: Date.now() });
    lastPublish.current = n;
  }, [publishPending, publishUnread, overview.data, canReview]);

  if (!canIncidents && !canReview) return null;
  const visit = (screen: string) => {
    onNavigate(screen);
    setOpen(false);
    setNotice(null);
  };
  const dot = unrecovered.length > 0 || pending > 0 || publishPending > 0 || publishUnread > 0;
  return (
    <>
      <IconButton className="lwi-notification-trigger" label="운영 알림" onClick={() => setOpen(true)}>
        <Bell size={17} />
        {dot && <span className="lwi-notice-dot" />}
      </IconButton>
      {notice && (
        <aside className="lwi-persistent-notice" aria-label="새 운영 알림" role="status">
          <div className="lwi-notice-icon">{notice.kind === "incident" ? <TriangleAlert size={20} /> : <ClipboardCheck size={20} />}</div>
          <div>
            <small>실시간 운영 알림 · {fmtTime(new Date(notice.at).toISOString())}</small>
            <strong>{notice.title}</strong>
            <p>{notice.body}</p>
            <Button size="sm" variant="secondary" onClick={() => visit(notice.kind === "incident" ? "monitoring" : notice.kind === "publish" ? "runs" : "reviews")}>
              {notice.kind === "incident" ? "모니터링 열기" : notice.kind === "publish" ? "작업 이력 열기" : "결과 검토함 열기"}
            </Button>
          </div>
          <IconButton label="알림 닫기" onClick={() => setNotice(null)}>
            <X size={15} />
          </IconButton>
        </aside>
      )}
      <Dialog open={open} onOpenChange={setOpen} title="운영 알림" footer={<DialogActions primary={{ label: "닫기", onClick: () => setOpen(false) }} />}>
        <div className="lwi-notice-list">
          {canIncidents && (
            <section>
              <TriangleAlert size={18} />
              <div>
                <strong>머신 장애 · 미복귀 {unrecovered.length}건</strong>
                <p>{unrecovered.length ? `${unrecovered[0]!.e.customerName} · ${unrecovered[0]!.e.machineLabel} · ${fmtTime(unrecovered[0]!.e.at)}` : "최근 24시간 미복귀 장애 없음"}</p>
                <Button size="sm" variant="secondary" onClick={() => visit("monitoring")}>
                  모니터링 열기
                </Button>
              </div>
            </section>
          )}
          {canReview && (
            <section>
              <ClipboardCheck size={18} />
              <div>
                <strong>
                  확인 대기 발행 {publishPending}건 · 새 답글 {publishUnread}건
                </strong>
                <p>고객 원고와 대화를 확인하세요.</p>
                <div className="lwd-actions">
                  {publishGroups.map((g) => (
                    <Button
                      key={g.id}
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        useCustomerDetailFocus.getState().open(g.id, "발행 요청");
                        visit("customers");
                      }}
                    >
                      {g.name} · 확인 {g.pending} / 답글 {g.unread}
                    </Button>
                  ))}
                  <Button size="sm" variant="ghost" onClick={() => visit("runs")}>
                    작업 이력 열기
                  </Button>
                </div>
              </div>
            </section>
          )}
          {canReview && (
            <section>
              <ClipboardCheck size={18} />
              <div>
                <strong>결과 검토 대기 {pending}건</strong>
                <p>완료 판정과 증거 캡처 확인{summary.data?.held ? ` · 보류 ${summary.data.held}건` : ""}</p>
                <Button size="sm" variant="secondary" onClick={() => visit("reviews")}>
                  결과 검토함 열기
                </Button>
              </div>
            </section>
          )}
          {rows
            .filter((r) => r.recovered)
            .slice(0, 3)
            .map((r) => (
              <section key={r.key}>
                <CheckCircle2 size={18} />
                <div>
                  <strong>{r.e.machineLabel} 연결 복구</strong>
                  <p>
                    {r.e.customerName} · {r.kind} · {r.events} · {fmtTime(r.e.at)}
                  </p>
                </div>
              </section>
            ))}
        </div>
      </Dialog>
    </>
  );
}

/** 전체 관리에서 알림의 목적 화면으로 이동. */
export function useAdminNavigate() {
  const navigate = useNavigate();
  return (screen: string) => void navigate({ to: "/admin/$screen", params: { screen } });
}
